import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, formatHour } from "@/lib/utils";
import { Calendar, Clock, MapPin, User, Check, X, DollarSign, ShieldCheck, Receipt, AlertTriangle, Share2, Phone, Activity, CreditCard, MessageCircle, Navigation, Copy } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ReceiptModal from "@/components/ReceiptModal";
import BackButton from "@/components/BackButton";
import { Textarea } from "@/components/ui/textarea";

// Fix Leaflet default marker icon (broken with bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isPaidStatus(ps) {
  return ps && !ps.startsWith("pending");
}

function displayStatus(booking) {
  if (booking.status === "checked_in") return "checked_in";
  const paid = isPaidStatus(booking.payment_status);
  const endHour = booking.end_hour;
  const dateStr = booking.date;
  const now = new Date();
  const bookingEnd = new Date(dateStr + "T" + String(Math.floor(endHour)).padStart(2, "0") + ":" + String(Math.round((endHour % 1) * 60)).padStart(2, "0") + ":00");
  if (booking.status === "confirmed" && paid && bookingEnd < now) return "completed";
  return booking.status;
}

function bookingStartDate(booking) {
  const startHour = booking.start_hour;
  return new Date(
    booking.date +
      "T" +
      String(Math.floor(startHour)).padStart(2, "0") +
      ":" +
      String(Math.round((startHour % 1) * 60)).padStart(2, "0") +
      ":00"
  );
}

const STATUS_STYLES = {
  confirmed: "border-volt/30 bg-volt/10 text-volt",
  pending: "border-yellow-400/30 bg-yellow-400/10 text-yellow-400",
  cancelled: "border-border bg-border/15 text-muted-foreground/80",
  rejected: "border-red-400/30 bg-red-400/10 text-red-400",
  checked_in: "border-sky-400/30 bg-sky-400/10 text-sky-400",
  completed: "border-emerald-400/30 bg-emerald-400/10 text-emerald-400",
};

const PAYMENT_STYLES = {
  mocked_paid: "border-emerald-400/30 bg-emerald-400/10 text-emerald-400",
  pending_cod: "border-yellow-400/30 bg-yellow-400/10 text-yellow-400",
  paid_at_court: "border-volt/30 bg-volt/10 text-volt",
  pending_online: "border-yellow-400/30 bg-yellow-400/10 text-yellow-400",
  refunded: "border-red-400/30 bg-red-400/10 text-red-400",
  partially_refunded: "border-orange-400/30 bg-orange-400/10 text-orange-400",
  cancelled_cod: "border-border bg-border/15 text-muted-foreground/80",
};

export default function BookingDetails() {
  const { t } = useTranslation();
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReceipt, setShowReceipt] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [playerCancelOpen, setPlayerCancelOpen] = useState(false);
  const [playerCancellationReason, setPlayerCancellationReason] = useState("");
  const [playerCancellationReasonError, setPlayerCancellationReasonError] = useState("");
  const [ownerCancellationReason, setOwnerCancellationReason] = useState("");
  const [ownerCancellationReasonError, setOwnerCancellationReasonError] = useState("");
  const [timeLeft, setTimeLeft] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [bookingUidCopied, setBookingUidCopied] = useState(false);

  useEffect(() => {
    if (!booking || booking.status !== 'confirmed') return;
    const tick = () => {
      const now = new Date();
      const start = new Date(booking.date);
      start.setHours(booking.start_hour, 0, 0, 0);
      const diff = start.getTime() - now.getTime();
      if (diff <= 0 || diff > 24 * 3600 * 1000) { setTimeLeft(''); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(`${h}h ${m}m`);
      setIsUrgent(diff < 3600000);
    };
    tick();
    const i = setInterval(tick, 30000);
    return () => clearInterval(i);
  }, [booking]);

  const load = () => {
    setLoading(true);
    api
      .get(`/bookings/${bookingId}`)
      .then((r) => setBooking(r.data))
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail));
        navigate("/bookings");
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [bookingId]);

  const isOwner = user?.role === "owner" || user?.role === "admin";
  const isOwnBooking = booking && booking.user_id === user?.id;
  const canAct = isOwner || user?.role === "admin";
  const isFutureBooking = booking ? bookingStartDate(booking).getTime() > Date.now() : false;

  const decide = async (decision) => {
    try {
      await api.patch(`/bookings/${bookingId}/decision`, null, { params: { decision } });
      toast.success(decision === "confirmed" ? t("common.booking_approved") : t("common.booking_rejected"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const markPaid = async () => {
    try {
      await api.patch(`/bookings/${bookingId}/mark-paid`);
      toast.success(t("common.booking_marked_paid"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openPlayerCancelDialog = () => {
    setPlayerCancellationReason("");
    setPlayerCancellationReasonError("");
    setPlayerCancelOpen(true);
  };

  const closePlayerCancelDialog = (open) => {
    setPlayerCancelOpen(open);
    if (!open) {
      setPlayerCancellationReasonError("");
    }
  };

  const handleOwnerCancelConfirmChange = (open) => {
    setCancelConfirm(open);
    if (!open) {
      setOwnerCancellationReason("");
      setOwnerCancellationReasonError("");
    }
  };

  const cancel = async () => {
    const reason = playerCancellationReason.trim();
    if (!reason) {
      setPlayerCancellationReasonError(t("booking_details.cancellation_reason_required"));
      return;
    }

    setCancelling(true);
    try {
      await api.patch(`/bookings/${bookingId}/cancel`, {
        cancellation_reason: reason,
      });
      toast.success(t("common.booking_cancelled"));
      setPlayerCancelOpen(false);
      setPlayerCancellationReason("");
      setPlayerCancellationReasonError("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  const cancelAsOwner = async () => {
    const reason = ownerCancellationReason.trim();
    if (!reason) {
      setOwnerCancellationReasonError(t("owner.cancel_reason_required"));
      return;
    }

    setCancelling(true);
    try {
      await api.patch(`/bookings/${bookingId}/owner-cancel`, {
        cancellation_reason: reason,
      });
      toast.success(t("common.booking_cancelled"));
      handleOwnerCancelConfirmChange(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  const refundBooking = async () => {
    try {
      const r = await api.post(`/bookings/${bookingId}/refund`);
      toast.success(t("owner.refund_success"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const checkIn = async () => {
    try {
      const r = await api.post(`/bookings/${bookingId}/checkin`);
      toast.success(t("owner.checked_in"));
      setBooking((prev) => ({ ...prev, ...r.data }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const checkOut = async () => {
    try {
      const r = await api.post(`/bookings/${bookingId}/checkout`);
      toast.success(t("owner.checked_out"));
      setBooking((prev) => ({ ...prev, ...r.data }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/s/${bookingId}`;
    const sportLabel = booking.court?.sport ? t(`home.sports.${booking.court.sport}`) : "";
    const text = `${t("booking_details.share_text")}\n${t("booking_details.court")}: ${booking.court_name}${sportLabel ? ` · ${sportLabel}` : ""}\n${t("booking_details.date")}: ${booking.date}\n${t("booking_details.time")}: ${formatHour(booking.start_hour)} – ${formatHour(booking.end_hour)}\n${t("booking_details.total")}: ${formatPrice(booking.total_price, booking.currency)}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: t("booking_details.share_title"), text: text, url: url });
        return;
      } catch (e) {
        if (e?.name !== "AbortError" && import.meta.env.DEV) console.warn("Share failed:", e);
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("booking_details.link_copied"));
    } catch {
      toast.error(t("booking_details.share_failed"));
    }
  };

  const handleCopyBookingUid = async () => {
    if (!booking?.booking_uid) return;

    try {
      await navigator.clipboard.writeText(booking.booking_uid);
      setBookingUidCopied(true);
      window.setTimeout(() => setBookingUidCopied(false), 2000);
    } catch {
      toast.error(t("booking_details.share_failed"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-background flex items-center justify-center">
        <div className="label-eyebrow animate-pulse">{t("common.loading")}</div>
      </div>
    );
  }

  if (!booking) return null;

  const court = booking.court;
  const savedCancellationReason = typeof booking.cancellation_reason === "string" ? booking.cancellation_reason.trim() : "";
  const showCancellationReason = Boolean(savedCancellationReason) && booking.status === "cancelled" && (isOwnBooking || canAct);
  const mapDestination = court?.lat && court?.lng
    ? `${court.lat},${court.lng}`
    : [court?.address, court?.city].filter(Boolean).join(", ");
  const viewOnMapHref = mapDestination ? `https://maps.google.com/?q=${encodeURIComponent(mapDestination)}` : null;
  const directionsHref = mapDestination ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapDestination)}` : null;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-10">
        <BackButton className="mb-8" />

        <div className="grid md:grid-cols-[1fr_380px] gap-10">
          {/* Main info */}
          <div>
            <div className="label-eyebrow text-volt mb-2">{t("booking_details.eyebrow")}</div>
            {timeLeft && (
              <div className={`flex items-center gap-2 mb-4 px-4 py-2 border ${isUrgent ? 'border-red-400/20 bg-red-400/5 text-red-400' : 'border-volt/20 bg-volt/5 text-volt'}`}>
                <Clock className="h-4 w-4" />
                <span className="text-sm font-semibold">{t("booking_details.starts_in", { time: timeLeft })}</span>
              </div>
            )}
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-6">
              {booking.court_name}
            </h1>

            {booking.booking_uid && (
              <div className="mb-6 border border-border/30 bg-card p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">
                      {t("booking_details.booking_uid")}
                    </div>
                    <div className="font-mono text-lg sm:text-xl font-semibold tracking-[0.2em] text-foreground break-all">
                      {booking.booking_uid}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyBookingUid}
                    className="inline-flex items-center justify-center gap-2 self-start border border-border px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-muted-foreground/80 transition-colors hover:border-volt hover:text-volt"
                  >
                    {bookingUidCopied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                    {bookingUidCopied ? t("common.copied") : t("common.copy")}
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <div className="border border-border/30 bg-card p-4">
                <Calendar className="h-4 w-4 text-volt mb-2" />
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">{t("booking_details.date")}</div>
                <div className="font-heading text-xl">{booking.date}</div>
              </div>
              <div className="border border-border/30 bg-card p-4">
                <Clock className="h-4 w-4 text-volt mb-2" />
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">{t("booking_details.time")}</div>
                <div className="font-heading text-xl font-mono">
                  {formatHour(booking.start_hour)} – {formatHour(booking.end_hour)}
                </div>
              </div>
              <div className="border border-border/30 bg-card p-4">
                <Activity className="h-4 w-4 text-volt mb-2" />
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">{t("booking_details.status")}</div>
                <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLES[displayStatus(booking)] || "border-border text-muted-foreground/80"}`}>
                  {t("bookings.status_" + displayStatus(booking), displayStatus(booking))}
                </span>
              </div>
              <div className="border border-border/30 bg-card p-4">
                <CreditCard className="h-4 w-4 text-volt mb-2" />
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">{t("booking_details.payment")}</div>
                <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 border ${PAYMENT_STYLES[booking.payment_status] || "border-border text-muted-foreground/80"}`}>
                  {t(`bookings.payment.${booking.payment_status}`, booking.payment_status)}
                </span>
              </div>
            </div>

            <div className="border border-border/30 bg-card p-5 mb-6 space-y-3">
              <div className="label-eyebrow">{t("booking_details.player_info")}</div>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-muted rounded-full flex items-center justify-center shrink-0">
                  <User className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="font-semibold min-w-0 truncate">{booking.user_name} {booking.is_manual && <span className="inline-block text-[9px] tracking-wider uppercase font-bold px-1.5 py-0.5 bg-volt/15 text-volt rounded-sm">Manual</span>}</div>
              </div>
              <div className="flex gap-2 pl-[52px]">
                <Link to="/chats" className="flex-1 border border-border/50 rounded-sm px-2.5 py-1.5 text-[10px] tracking-widest uppercase font-bold text-muted-foreground hover:text-volt hover:border-volt/50 transition-colors flex items-center justify-center gap-1">
                  <MessageCircle className="h-3.5 w-3.5" />{t("owner.message")}
                </Link>
                {booking.user_phone && <a href={`tel:${booking.user_phone}`} title={booking.user_phone} className="flex-1 border border-border/50 rounded-sm px-2.5 py-1.5 text-[10px] tracking-widest uppercase font-bold text-muted-foreground hover:text-volt hover:border-volt/50 transition-colors flex items-center justify-center gap-1"><Phone className="h-3.5 w-3.5" />{t("owner.call")}</a>}
              </div>
            </div>

            {booking.selected_extras && booking.selected_extras.length > 0 && (
              <div className="border border-border/30 bg-card p-5 mb-6">
                <div className="label-eyebrow mb-4">{t("court.extras")}</div>
                <div className="space-y-2">
                  {booking.selected_extras.map((extra, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-border/30 pb-2 last:border-0">
                      <div>
                        <div className="text-sm font-semibold">{extra.name}</div>
                        {extra.description && <div className="text-[10px] text-muted-foreground/80">{extra.description}</div>}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                        {extra.qty > 1 && <span className="text-[10px] text-muted-foreground/80">x{extra.qty}</span>}
                        {extra.price > 0 && (
                          <div className="text-volt font-bold text-sm">
                            +{formatPrice(extra.price * (extra.qty || 1), booking.currency)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {booking.extras_total > 0 && (
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <span className="text-[10px] tracking-widest uppercase text-muted-foreground/80">{t("booking_details.extras_total")}</span>
                      <span className="text-foreground font-bold">{formatPrice(booking.extras_total, booking.currency)}</span>
                    </div>
                  )}
                  {booking.discount_label && booking.discount_amount > 0 && (
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <span className="text-[10px] tracking-widest uppercase text-emerald-400">{t("booking_details.discount")} · {booking.discount_label}</span>
                      <span className="text-emerald-400 font-bold">&minus;{formatPrice(booking.discount_amount, booking.currency)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {court && (
              <div className="border border-border/30 bg-card p-5">
                <div className="label-eyebrow mb-4">{t("booking_details.court_info")}</div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4 text-muted-foreground/80" />
                    {court.address}, {court.city}
                  </div>
                  {court.indoor !== undefined && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <ShieldCheck className="h-4 w-4 text-muted-foreground/80" />
                      {court.indoor ? t("court.indoor") : t("court.outdoor")}
                    </div>
                  )}
                  {court.lat && court.lng && (
                    <div className="h-48 w-full rounded-sm overflow-hidden mt-2 z-0 relative">
                      <style>{`.booking-map .leaflet-pane { z-index: 0 !important; }`}</style>
                      <div className="booking-map h-full w-full">
                        <MapContainer center={[court.lat, court.lng]} zoom={15} scrollWheelZoom={false} className="h-full w-full" key={`${court.lat}-${court.lng}`}>
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <Marker position={[court.lat, court.lng]}>
                            <Popup>{booking.court_name}</Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    </div>
                  )}
                  {(viewOnMapHref || directionsHref) && (
                    <div className="flex flex-wrap items-center gap-4 mt-1">
                      {viewOnMapHref && (
                        <a
                          href={viewOnMapHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-volt hover:underline"
                        >
                          <MapPin className="h-3.5 w-3.5" /> {t("booking_details.view_on_map")}
                        </a>
                      )}
                      {directionsHref && (
                        <a
                          href={directionsHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-volt hover:underline"
                        >
                          <Navigation className="h-3.5 w-3.5" /> {t("bookings.get_directions")}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {booking.cancellation_policy && (
              <div className="border border-border bg-card p-4 mt-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t("booking_details.cancellation_policy")}</span>
                  <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 border border-volt/30 text-volt">
                    {booking.cancellation_policy}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground/60 mt-1">
                  {booking.free_cancel_hours
                    ? t("booking_details.free_cancel_up_to", { hours: booking.free_cancel_hours })
                    : t("booking_details.non_refundable")}
                </p>
              </div>
            )}

            {(booking.host_whatsapp || booking.host_phone) && (() => {
              const canShow = booking.host_contact_visibility === "always" || ["confirmed", "checked_in", "completed"].includes(booking.status);
              return (
                <div className="border border-border/30 bg-card p-5 mt-6">
                  <div className="label-eyebrow mb-3">{t("booking_details.host_contact")}</div>
                  {!canShow ? (
                    <div className="text-sm text-muted-foreground/80">
                      {t("booking_details.contact_available_after_confirmation")}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {booking.host_whatsapp && (
                        <a
                          href={`https://wa.me/${booking.host_whatsapp.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 border border-emerald-400/30 text-emerald-400 text-xs hover:bg-emerald-400/5 transition-colors"
                        >
                          <MessageCircle className="h-4 w-4" /> {t("booking_details.contact_whatsapp")}
                        </a>
                      )}
                      {booking.host_phone && (
                        <a
                          href={`tel:${booking.host_phone}`}
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-volt transition-colors"
                        >
                          <Phone className="h-4 w-4" />
                          {booking.host_phone}
                        </a>
                      )}
                      {booking.court_id && (
                        <Link
                          to="/chats"
                          className="flex items-center gap-2 px-4 py-2 border border-border text-muted-foreground text-xs hover:bg-muted transition-colors"
                        >
                          <MessageCircle className="h-4 w-4" /> {t("booking_details.chat")}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Sidebar — total + actions */}
          <div>
            <div className="border border-border bg-card p-6 sticky top-20">
              <div className="label-eyebrow mb-2">{t("booking_details.total")}</div>
              <div className="font-heading text-6xl text-volt mb-6">
                {formatPrice(booking.total_price, booking.currency)}
              </div>

              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("booking_details.date")}</span>
                  <span>{booking.date}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("booking_details.time")}</span>
                  <span className="font-mono">{formatHour(booking.start_hour)} – {formatHour(booking.end_hour)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("booking_details.payment_method")}</span>
                  <span className="uppercase text-[10px] tracking-widest">{t("bookings.payment_method_" + booking.payment_method, booking.payment_method)}</span>
                </div>
                {booking.discount_label && booking.discount_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-400">{booking.discount_label}</span>
                    <span className="text-emerald-400">&minus;{formatPrice(booking.discount_amount, booking.currency)}</span>
                  </div>
                )}
                {booking.selected_extras && booking.selected_extras.length > 0 && (
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mb-1">{t("court.extras")}</div>
                    {booking.selected_extras.map((extra, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{extra.name}{extra.qty > 1 ? ` x${extra.qty}` : ""}</span>
                        {extra.price > 0 && <span className="text-volt">+{formatPrice(extra.price * (extra.qty || 1), booking.currency)}</span>}
                      </div>
                    ))}
                    {booking.extras_total > 0 && (
                      <div className="flex justify-between text-sm border-t border-border pt-1 mt-1">
                        <span className="text-muted-foreground/80 text-[10px] tracking-widest uppercase">{t("booking_details.extras_total")}</span>
                        <span className="text-foreground font-bold">{formatPrice(booking.extras_total, booking.currency)}</span>
                      </div>
                    )}
                  </div>
                )}
                {showCancellationReason && (
                  <div className="border-t border-border pt-4 mt-4">
                    <div className="border border-red-400/20 bg-red-400/5 p-4">
                      <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-red-400/80 mb-2">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        <span>{t("booking_details.cancellation_reason_label")}</span>
                      </div>
                      <p className="text-sm leading-6 text-foreground/90 whitespace-pre-wrap break-words">
                        {savedCancellationReason}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 space-y-2">
                <button
                  onClick={() => setShowReceipt(true)}
                  className="btn-volt rounded-sm px-6 py-3 text-xs w-full flex items-center justify-center gap-2"
                >
                  <Receipt className="h-3.5 w-3.5" /> {t("booking_details.view_receipt")}
                </button>

                <button
                  onClick={handleShare}
                  className="btn-outline rounded-sm w-full py-2.5 text-xs flex items-center justify-center gap-2"
                >
                  <Share2 className="h-3.5 w-3.5" /> {t("booking_details.share")}
                </button>

                {isOwnBooking && booking.status === "confirmed" && isFutureBooking && (
                  <>
                    <div className="border-t border-border" />
                    <button
                      onClick={openPlayerCancelDialog}
                      className="btn-outline rounded-sm w-full py-2.5 text-xs border-red-400 text-red-400 hover:bg-red-400 hover:text-primary-foreground flex items-center justify-center gap-2"
                    >
                      <X className="h-3.5 w-3.5" /> {t("booking_details.cancel_booking")}
                    </button>
                  </>
                )}

                {canAct && (
                  <div className="border border-border bg-card p-4 mt-4">
                    <div className="label-eyebrow mb-3">{t("booking_details.host_actions")}</div>
                    <div className="space-y-2">
                      {booking.status === "pending" && (
                        <>
                          <button
                            onClick={() => decide("confirmed")}
                            className="w-full bg-volt text-primary-foreground py-3 text-xs tracking-widest uppercase font-bold flex items-center justify-center gap-2"
                          >
                            <Check className="h-3.5 w-3.5" /> {t("owner.approve")}
                          </button>
                          <div className="border-t border-border/30" />
                          <button
                            onClick={() => decide("rejected")}
                            className="w-full border border-red-400 text-red-400 py-3 text-xs tracking-widest uppercase font-bold flex items-center justify-center gap-2 hover:bg-red-400 hover:text-primary-foreground"
                          >
                            <X className="h-3.5 w-3.5" /> {t("owner.reject")}
                          </button>
                        </>
                      )}

                      {booking.payment_status === "pending_cod" && booking.status !== "cancelled" && booking.status !== "rejected" && (
                        <button
                          onClick={markPaid}
                          className="btn-outline rounded-sm w-full py-3 text-xs border-volt text-volt hover:bg-volt hover:text-primary-foreground flex items-center justify-center gap-2"
                        >
                          <DollarSign className="h-3.5 w-3.5" /> {t("owner.mark_paid")}
                        </button>
                      )}

                      {booking.status === "confirmed" && (
                        <button
                          onClick={checkIn}
                          className="btn-outline rounded-sm w-full py-3 text-xs border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-primary-foreground flex items-center justify-center gap-2"
                        >
                          <User className="h-3.5 w-3.5" /> {t("owner.check_in")}
                        </button>
                      )}
                      {booking.status === "checked_in" && (
                        <button
                          onClick={checkOut}
                          className="btn-outline rounded-sm w-full py-3 text-xs border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-primary-foreground flex items-center justify-center gap-2"
                        >
                          <Check className="h-3.5 w-3.5" /> {t("owner.check_out")}
                        </button>
                      )}

                      {(booking.status !== "cancelled" && booking.status !== "rejected" && booking.status !== "pending") && (
                        <div className="border-t border-red-400/20 pt-2 mt-2">
                          <button
                            onClick={() => setCancelConfirm(true)}
                            disabled={cancelling}
                            className="btn-outline rounded-sm w-full py-3 text-xs border-red-400/40 text-red-400/60 hover:border-red-400 hover:text-red-400 disabled:opacity-40 flex items-center justify-center gap-2"
                          >
                            <X className="h-3.5 w-3.5" /> {t("owner.cancel_booking")}
                          </button>
                        </div>
                      )}

                      {(booking.payment_status === "paid_at_court" || booking.payment_status === "mocked_paid") && booking.status !== "cancelled" && booking.status !== "rejected" && booking.status !== "pending" && (
                        <button
                          onClick={refundBooking}
                          className="btn-outline rounded-sm w-full py-3 text-xs border-yellow-400/40 text-yellow-400/60 hover:border-yellow-400 hover:text-yellow-400 flex items-center justify-center gap-2"
                        >
                          <DollarSign className="h-3.5 w-3.5" /> {t("owner.refund")}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <Link
                  to={`/courts/${booking.court_id}`}
                  className="btn-outline rounded-sm w-full py-3 text-xs flex items-center justify-center gap-2"
                >
                  <Calendar className="h-3.5 w-3.5" /> {t("booking_details.view_court")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AlertDialog open={cancelConfirm} onOpenChange={handleOwnerCancelConfirmChange}>
        <AlertDialogContent className="bg-background border border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              {t("owner.cancel_title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground/80 text-sm">
              {t("owner.cancel_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label htmlFor="owner-cancellation-reason" className="text-sm font-medium">
              {t("owner.cancel_reason_label")}
            </label>
            <Textarea
              id="owner-cancellation-reason"
              value={ownerCancellationReason}
              onChange={(event) => {
                setOwnerCancellationReason(event.target.value);
                if (ownerCancellationReasonError && event.target.value.trim()) {
                  setOwnerCancellationReasonError("");
                }
              }}
              placeholder={t("owner.cancel_reason_placeholder")}
              disabled={cancelling}
              className={ownerCancellationReasonError ? "border-red-400 focus-visible:ring-red-400" : ""}
            />
            {ownerCancellationReasonError ? (
              <p className="text-xs text-red-400">{ownerCancellationReasonError}</p>
            ) : (
              <p className="text-xs text-muted-foreground/80">
                {t("owner.cancel_reason_helper")}
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-border text-muted-foreground/80 hover:bg-card hover:text-foreground">
              {t("common.close")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                if (!ownerCancellationReason.trim()) {
                  event.preventDefault();
                  setOwnerCancellationReasonError(t("owner.cancel_reason_required"));
                  return;
                }
                cancelAsOwner();
              }}
              disabled={cancelling}
              className="bg-red-500 text-foreground hover:bg-red-600"
            >
              {cancelling ? t("common.loading") : t("owner.cancel_booking")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={playerCancelOpen} onOpenChange={closePlayerCancelDialog}>
        <AlertDialogContent className="bg-background border border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              {t("booking_details.cancel_title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground/80 text-sm">
              {t("booking_details.cancel_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label htmlFor="cancellation-reason" className="text-sm font-medium">
              {t("booking_details.cancellation_reason_label")}
            </label>
            <Textarea
              id="cancellation-reason"
              value={playerCancellationReason}
              onChange={(event) => {
                setPlayerCancellationReason(event.target.value);
                if (playerCancellationReasonError && event.target.value.trim()) {
                  setPlayerCancellationReasonError("");
                }
              }}
              placeholder={t("booking_details.cancellation_reason_placeholder")}
              disabled={cancelling}
              className={playerCancellationReasonError ? "border-red-400 focus-visible:ring-red-400" : ""}
            />
            {playerCancellationReasonError ? (
              <p className="text-xs text-red-400">{playerCancellationReasonError}</p>
            ) : (
              <p className="text-xs text-muted-foreground/80">
                {t("booking_details.cancellation_reason_helper")}
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={cancelling}
              className="bg-transparent border border-border text-muted-foreground/80 hover:bg-card hover:text-foreground"
            >
              {t("common.close")}
            </AlertDialogCancel>
            <button
              type="button"
              onClick={cancel}
              disabled={cancelling}
              className="inline-flex h-10 items-center justify-center rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-red-600 disabled:pointer-events-none disabled:opacity-50"
            >
              {cancelling ? t("common.loading") : t("booking_details.cancel_booking")}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReceiptModal
        open={showReceipt}
        onOpenChange={setShowReceipt}
        booking={booking}
        t={t}
      />
    </div>
  );
}
